import { readRequiredAuthMemberRoleArray } from "@airange/shared/auth-member-roles";
import { ApiError } from "@airange/shared/api";
import type { operations } from "@airange/generated-contracts/ctf";

type CtfOperation<Name extends keyof operations> = operations[Name];
type OperationQuery<Name extends keyof operations> = NonNullable<
  CtfOperation<Name>["parameters"]["query"]
>;

export type CtfCurrentUserResponse =
  CtfOperation<"bootstrap_auth_session_api_auth__application_key__bootstrap_post">["responses"][200]["content"]["application/json"];
export type CtfCategoryListResponse =
  CtfOperation<"get_teacher_categories_api_challenges_teacher_categories_get">["responses"][200]["content"]["application/json"];
export type CtfCategoryMutationResponse =
  CtfOperation<"post_challenge_category_api_challenges_categories_post">["responses"][201]["content"]["application/json"];
export type CtfCategoryArchiveResponse =
  CtfOperation<"post_challenge_category_archive_api_challenges_categories__category_code__archive_post">["responses"][200]["content"]["application/json"];
export type CtfChallengeListResponse =
  CtfOperation<"get_teacher_challenges_api_challenges_teacher_get">["responses"][200]["content"]["application/json"];
export type CtfChallengeMutationResponse =
  CtfOperation<"post_challenge_api_challenges_post">["responses"][201]["content"]["application/json"];
export type CtfChallengeVersionMutationResponse =
  CtfOperation<"post_challenge_version_api_challenges__challenge_code__versions_post">["responses"][201]["content"]["application/json"];
export type CtfArtifactReleaseListResponse =
  CtfOperation<"get_teacher_artifact_releases_api_challenges_teacher_artifact_releases_get">["responses"][200]["content"]["application/json"];
export type CtfArtifactReleaseMutationResponse =
  CtfOperation<"post_artifact_release_api_challenges_artifact_releases_post">["responses"][201]["content"]["application/json"];
export type CtfVersionArtifactBindResponse =
  CtfOperation<"post_version_artifact_release_api_challenges__challenge_code__versions__version_no__artifact_release_post">["responses"][200]["content"]["application/json"];
export type CtfChallengeVersionPublishResponse =
  CtfOperation<"post_challenge_version_publish_api_challenges__challenge_code__versions__version_no__publish_post">["responses"][200]["content"]["application/json"];

export type CtfCurrentUser = NonNullable<CtfCurrentUserResponse>;
export type CtfCurrentUserRoleSource = Pick<CtfCurrentUser, "application_roles">;
const CTF_CURRENT_USER_ROLE_KEYS = [
  "ctf_learner",
  "ctf_teacher",
  "ctf_admin",
  "ctf_manager",
] as const satisfies readonly CtfCurrentUser["application_roles"][number][];
export type CtfCurrentUserRoleKey = CtfCurrentUser["application_roles"][number];
export type CtfDisplayUser = CtfCurrentUser & {
  username: string;
  full_name?: string | null;
  avatar_url?: string | null;
};
export type CtfChallengeCategoryRead = CtfCategoryListResponse["items"][number];
export type CtfChallengeRead = CtfChallengeListResponse["items"][number];
export type CtfArtifactReleaseRead = CtfArtifactReleaseListResponse["items"][number];
export type CtfChallengeVersionRead = CtfChallengeVersionMutationResponse;

export type CtfChallengeCategoryStatus = CtfChallengeCategoryRead["status"];
export type CtfChallengeStatus = CtfChallengeRead["status"];
export type CtfArtifactReleaseStatus = CtfArtifactReleaseRead["status"];
export type CtfChallengeVersionStatus = CtfChallengeVersionRead["status"];

export function readCtfCurrentUserApplicationRoles(
  currentUser: CtfCurrentUserRoleSource,
  path = "current_user.application_roles",
): CtfCurrentUserRoleKey[] {
  return readRequiredAuthMemberRoleArray(currentUser, {
    allowedRoles: CTF_CURRENT_USER_ROLE_KEYS,
    path,
    createError: (issue) =>
      new ApiError(500, {
        code: "ctf.auth.data_contract_invalid",
        message: issue.message,
      }),
  });
}

export function toCtfDisplayUser(user: CtfCurrentUser): CtfDisplayUser {
  return {
    ...user,
    username: user.username ?? user.user_id,
    full_name: user.display_name ?? user.username ?? user.user_id,
  };
}

export type CtfCategoryListQuery = Omit<
  OperationQuery<"get_teacher_categories_api_challenges_teacher_categories_get">,
  "page" | "page_size"
>;
export type CtfChallengeListQuery = Omit<
  OperationQuery<"get_teacher_challenges_api_challenges_teacher_get">,
  "page" | "page_size"
>;
export type CtfArtifactReleaseListQuery = Omit<
  OperationQuery<"get_teacher_artifact_releases_api_challenges_teacher_artifact_releases_get">,
  "page" | "page_size"
>;

export type CtfChallengeCategoryCreateInput =
  CtfOperation<"post_challenge_category_api_challenges_categories_post">["requestBody"]["content"]["application/json"];
export type CtfChallengeCreateInput =
  CtfOperation<"post_challenge_api_challenges_post">["requestBody"]["content"]["application/json"];
export type CtfChallengeVersionCreateBody =
  CtfOperation<"post_challenge_version_api_challenges__challenge_code__versions_post">["requestBody"]["content"]["application/json"];
export type CtfChallengeVersionCreateInput = Omit<
  CtfChallengeVersionCreateBody,
  "version_no" | "delivery_type"
> &
  Partial<Pick<CtfChallengeVersionCreateBody, "version_no" | "delivery_type">>;
export type CtfArtifactReleaseRegisterInput =
  CtfOperation<"post_artifact_release_api_challenges_artifact_releases_post">["requestBody"]["content"]["application/json"];
export type CtfChallengeVersionArtifactBindInput =
  CtfOperation<"post_version_artifact_release_api_challenges__challenge_code__versions__version_no__artifact_release_post">["requestBody"]["content"]["application/json"];
